/**
 * Funnel badges (TASK-238)
 *
 * Small colored badges for a client's funnel stage and lifecycle outcome.
 * The channel badge is reused from the dashboard (ChannelBadge).
 */
import { cn } from '@/lib/utils';
import type { FunnelOutcome } from '@/types/funnel';

/**
 * Russian labels of the known funnel stages (codename -> label): the legacy
 * estimator stages and the KAN-682 single e-commerce funnel catalogue.
 * The backend label (when sent) wins over this table.
 */
const STAGE_LABELS: Record<string, string> = {
  // Legacy estimator funnel.
  lead: 'Лид',
  qualified: 'Квалифицирован',
  offer: 'Предложение',
  scheduled: 'Запланирован',
  won: 'Сделка',
  repeat: 'Повторный',
  // KAN-682: single e-commerce funnel (KAN-737: replied, order_placed = Продажа).
  cold: 'Холодный контакт',
  replied: 'Ответил',
  inbound: 'Входящее обращение',
  dm_reached: 'Вышли на ЛПР',
  interested: 'Интерес',
  info_sent: 'Отправлены материалы',
  callback_later: 'Перезвон позже',
  handed_to_manager: 'Передан менеджеру',
  order_placed: 'Продажа',
  upsell: 'Допродажа',
  loyal: 'Постоянный',
  lost: 'Отказ',
};

const STAGE_CLASS: Record<string, string> = {
  lead: 'bg-gray-100 text-gray-800',
  qualified: 'bg-indigo-100 text-indigo-800',
  offer: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-amber-100 text-amber-800',
  won: 'bg-green-100 text-green-800',
  repeat: 'bg-emerald-100 text-emerald-800',
  cold: 'bg-gray-100 text-gray-800',
  replied: 'bg-cyan-100 text-cyan-800',
  inbound: 'bg-sky-100 text-sky-800',
  dm_reached: 'bg-indigo-100 text-indigo-800',
  interested: 'bg-blue-100 text-blue-800',
  info_sent: 'bg-violet-100 text-violet-800',
  callback_later: 'bg-amber-100 text-amber-800',
  handed_to_manager: 'bg-orange-100 text-orange-800',
  order_placed: 'bg-green-100 text-green-800',
  upsell: 'bg-emerald-100 text-emerald-800',
  loyal: 'bg-teal-100 text-teal-800',
  lost: 'bg-red-100 text-red-800',
};

interface StageBadgeProps {
  /** Stage codename (e.g. "lead"). */
  stage: string | null | undefined;
  /** Optional override label (when backend sends a custom label). */
  label?: string | null;
  className?: string;
}

/**
 * Renders a colored badge for a funnel stage.
 */
export function StageBadge({ stage, label, className }: StageBadgeProps) {
  if (!stage) {
    return (
      <span
        data-testid="stage-badge"
        data-stage="none"
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500',
          className
        )}
      >
        -
      </span>
    );
  }
  const text = label || STAGE_LABELS[stage] || stage;
  const cls = STAGE_CLASS[stage] ?? 'bg-gray-100 text-gray-800';
  return (
    <span
      data-testid="stage-badge"
      data-stage={stage}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        cls,
        className
      )}
    >
      {text}
    </span>
  );
}

const OUTCOME_LABELS: Record<FunnelOutcome, string> = {
  open: 'Открытые',
  won: 'Выиграно',
  lost: 'Потеряно',
};

const OUTCOME_CLASS: Record<FunnelOutcome, string> = {
  open: 'bg-blue-100 text-blue-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
};

interface OutcomeBadgeProps {
  outcome: FunnelOutcome | null | undefined;
  className?: string;
}

/**
 * Renders a colored badge for a client's lifecycle outcome.
 */
export function OutcomeBadge({ outcome, className }: OutcomeBadgeProps) {
  if (!outcome) return null;
  return (
    <span
      data-testid="outcome-badge"
      data-outcome={outcome}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        OUTCOME_CLASS[outcome],
        className
      )}
    >
      {OUTCOME_LABELS[outcome]}
    </span>
  );
}

interface FollowupBadgeProps {
  /** ISO datetime of the scheduled follow-up call (or null/undefined when none). */
  followupAt: string | null | undefined;
  /** Optional short note (e.g. "днём, обсудить смету"). */
  note?: string | null;
  /** KAN-714: codename of the next step ("followup_message", "callback", ...); "" or absent: unknown. */
  followupType?: string | null;
  /** KAN-714: Russian label of the next step from the backend (null when the type is empty). */
  followupTypeLabel?: string | null;
  className?: string;
}

/**
 * KAN-714: caption of the badge by the next-step type. Without a type the old
 * "Созвон" caption is kept (older backend, legacy follow-ups).
 */
function followupCaption(type?: string | null, label?: string | null): string {
  if (type === 'followup_message') return '✉ Написать';
  if (type === 'callback' || !type) return '📞 Созвон';
  return `📅 ${label || type}`;
}

/**
 * Renders a badge for a scheduled next step with a client (message, call, visit).
 *
 * Renders nothing when no follow-up is scheduled. When the scheduled time is
 * already in the past, the badge switches to a red "overdue" style and exposes
 * a `data-overdue="true"` attribute so the missed call stands out.
 */
export function FollowupBadge({
  followupAt,
  note,
  followupType,
  followupTypeLabel,
  className,
}: FollowupBadgeProps) {
  if (!followupAt) return null;

  const date = new Date(followupAt);
  if (Number.isNaN(date.getTime())) return null;
  const isOverdue = date.getTime() < Date.now();
  const shortDate = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
  const caption = followupCaption(followupType, followupTypeLabel);
  const text = note ? `${caption}: ${shortDate}, ${note}` : `${caption}: ${shortDate}`;

  return (
    <span
      data-testid="followup-badge"
      data-overdue={isOverdue ? 'true' : undefined}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        isOverdue ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800',
        className
      )}
    >
      {text}
    </span>
  );
}
